/**
 * @file KanbanBoardPage.tsx
 * @description Доска сделок (phase09; phase19.31 — минимум: доска остаётся,
 * на всю ширину экрана по SC55).
 *
 * Заголовок — в верхней панели (`CabinetPageHeader`), главное действие
 * «+ Сделка» — то же окно, что в реестре сделок. Воронка выбирается
 * `SelectField` в строке над доской (не нативный список рядом с
 * заголовком). От 1024 — полотно колонок с перетаскиванием
 * (`DealsBoardCanvas`, прокрутка вбок только внутри полотна); уже — стадии
 * пилюлями и одна колонка на экран (`DealsBoardMobile`): страница не
 * прокручивается по горизонтали.
 *
 * Перенос в стадию «Не состоялась» / «Отменена» открывает окно причины
 * (`DealStageMoveDialog`): без причины сервер сделку не закрывает.
 */

import { Plus, RefreshCw, Receipt, Settings2, Workflow } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { useHasPermission } from "../../../auth/hooks";
import { DealsBoardCanvas } from "../../../components/crm/board/DealsBoardCanvas";
import { DealsBoardMobile } from "../../../components/crm/board/DealsBoardMobile";
import { useDealsBoard } from "../../../components/crm/board/useDealsBoard";
import { DealStageMoveDialog } from "../../../components/crm/deal-card/DealStageMoveDialog";
import { DealCreateDialog } from "../../../components/crm/deals/DealCreateDialog";
import { useFluidLayout } from "../../../components/ui/AppShell";
import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { EmptyState } from "../../../components/ui/EmptyState";
import { ListError, ListSkeleton } from "../../../components/ui/ListStates";
import type { PageAction } from "../../../components/ui/page-actions";
import { SelectField } from "../../../components/ui/SelectField";
import { BELOW_LG_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";

export function KanbanBoardPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const base = `/shop/${slug}`;
  const canUse = useHasPermission("SHOP_USE_CRM");
  const compact = useMediaQuery(BELOW_LG_QUERY);
  // Доски — на всю ширину экрана (SC55, исключение «кроме досок»).
  useFluidLayout();
  const board = useDealsBoard();
  const [creating, setCreating] = useState(false);
  const dealHref = (dealId: string) => `${base}/crm/deals/${dealId}`;

  const menu: PageAction[] = [
    { key: "list", label: "Реестр сделок", icon: Receipt, to: `${base}/crm/deals` },
    { key: "pipelines", label: "Воронки и стадии", icon: Settings2, to: `${base}/crm/pipelines` },
    { key: "refresh", label: "Обновить", icon: RefreshCw, run: board.reload, loading: board.loading },
  ];
  const action =
    canUse && board.pipelines.length > 0 ? (
      <Button variant="brand" onClick={() => setCreating(true)}>
        <Plus className="size-4" />
        Сделка
      </Button>
    ) : undefined;

  const header = (
    <CabinetPageHeader title="Доска сделок" menu={menu} menuLabel="Другие действия с доской" action={action} />
  );

  if (!board.loading && board.pipelines.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        {board.error ? (
          <ListError message={board.error} onRetry={board.reload} />
        ) : (
          <EmptyState
            icon={Workflow}
            title="Воронок сделок ещё нет"
            description="Доска показывает сделки по стадиям воронки. Создайте воронку в настройках"
            action={
              <Button variant="outline" asChild>
                <Link to={`${base}/crm/pipelines`}>Перейти к воронкам</Link>
              </Button>
            }
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {header}

      {board.pipelines.length > 1 ? (
        <SelectField
          ariaLabel="Воронка"
          value={board.pipelineId}
          onChange={board.setPipelineId}
          options={board.pipelines.map((p) => ({ value: p.id, label: p.name }))}
          className="w-full md:w-[280px]"
        />
      ) : null}

      {board.error ? <ListError message={board.error} onRetry={board.reload} /> : null}

      {board.loading && board.deals.length === 0 ? (
        <ListSkeleton rows={6} compact />
      ) : compact ? (
        <DealsBoardMobile stages={board.stages} deals={board.deals} dealHref={(d) => dealHref(d.id)} />
      ) : (
        <DealsBoardCanvas board={board} onCardClick={(d) => navigate(dealHref(d.id))} />
      )}

      {/* Перенос в «Не состоялась» / «Отменена» — сначала причина; «Отмена» оставляет карточку на месте. */}
      {board.pendingMove ? (
        <DealStageMoveDialog
          key={`${board.pendingMove.deal.id}:${board.pendingMove.stageId}`}
          deal={board.pendingMove.deal}
          stages={board.stages}
          targetStageId={board.pendingMove.stageId}
          onClose={board.cancelMove}
          onMoved={board.completeMove}
        />
      ) : null}

      {creating ? (
        <DealCreateDialog
          pipelines={board.pipelines}
          initialPipelineId={board.pipelineId}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            board.reload();
          }}
        />
      ) : null}
    </div>
  );
}
